(() => {
  const BOOKING_TIME_ZONE_OFFSET_HOURS = 7;
  let googleMapsLoader;

  const loadGoogleMaps = (apiKey) => {
    if (globalThis.google?.maps?.importLibrary) return Promise.resolve(globalThis.google.maps);
    if (googleMapsLoader) return googleMapsLoader;
    googleMapsLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async`;
      script.async = true;
      script.onload = () => resolve(globalThis.google.maps);
      script.onerror = () => reject(new Error('Google Maps could not be loaded.'));
      document.head.append(script);
    });
    return googleMapsLoader;
  };

  const parseJSON = (element) => {
    try {
      return JSON.parse(element.textContent);
    } catch (_error) {
      return null;
    }
  };

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const getBangkokDateParts = (date = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return { year: Number(values.year), month: Number(values.month), day: Number(values.day) };
  };

  const formatDateValue = ({ year, month, day }) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const addCalendarDays = (dateParts, days) => {
    const date = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day + days));
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
  };

  const createBookingReference = () => {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID().toUpperCase();
    return `GTP-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  };

  const initBookingForm = (form) => {
    if (form.dataset.gtpBookingInitialized === 'true') return;
    form.dataset.gtpBookingInitialized = 'true';
    const section = form.closest('.gtp-product');
    const dataElement = section?.querySelector('[data-gtp-booking-data]');
    const data = dataElement ? parseJSON(dataElement) : null;
    if (!section || !data) return;

    const packageSelect = form.querySelector('[data-gtp-package]');
    const departureWrap = form.querySelector('[data-gtp-departure-wrap]');
    const departureSelect = form.querySelector('[data-gtp-departure]');
    const datePicker = form.querySelector('[data-gtp-date-picker]');
    const dateInput = form.querySelector('[data-gtp-date]');
    const dateDisplay = form.querySelector('[data-gtp-date-display]');
    const adultsInput = form.querySelector('[data-gtp-adults]');
    const childrenInput = form.querySelector('[data-gtp-children]');
    const firstNameInput = form.querySelector('[data-gtp-first-name]');
    const lastNameInput = form.querySelector('[data-gtp-last-name]');
    const phoneCodeSelect = form.querySelector('[data-gtp-phone-code]');
    const phoneNumberInput = form.querySelector('[data-gtp-phone-number]');
    const pickupInput = form.querySelector('[data-gtp-pickup]');
    const pickupAutocompleteHost = form.querySelector('[data-gtp-pickup-autocomplete]');
    const requestsInput = form.querySelector('[data-gtp-requests]');
    const breakdown = form.querySelector('[data-gtp-breakdown]');
    const total = form.querySelector('[data-gtp-total]');
    const errorBox = form.querySelector('[data-gtp-error]');
    const submitButton = form.querySelector('[data-gtp-submit]');
    const mobileTotal = section.querySelector('[data-gtp-mobile-total]');
    const mobileNote = section.querySelector('[data-gtp-mobile-note]');

    const optionNames = Array.isArray(data.options) ? data.options.map(normalize) : [];
    const packageIndex = optionNames.indexOf('package');
    const travelerIndex = optionNames.indexOf('traveler type');
    const departureIndex = optionNames.indexOf('departure time');
    const hasDepartureOption = departureIndex >= 0;
    const variants = Array.isArray(data.variants) ? data.variants : [];
    const blackoutDates = new Set(Array.isArray(data.blackoutDates) ? data.blackoutDates.map(String) : []);
    const availableDates = new Set(Array.isArray(data.availableDates) ? data.availableDates.map(String) : []);
    const cutoffHours = data.cutoffHours === '' || data.cutoffHours === null ? Number.NaN : Number(data.cutoffHours);
    const money = new Intl.NumberFormat(data.locale || 'en', {
      style: 'currency',
      currency: data.currency || 'USD',
    });

    const showError = (message) => {
      errorBox.textContent = message;
      errorBox.hidden = !message;
    };

    const initializePickupAutocomplete = async () => {
      if (!pickupInput || !pickupAutocompleteHost || !data.googleMapsApiKey) return;
      try {
        const maps = await loadGoogleMaps(data.googleMapsApiKey);
        const { PlaceAutocompleteElement } = await maps.importLibrary('places');
        const autocomplete = new PlaceAutocompleteElement({ includedRegionCodes: ['th'] });
        autocomplete.placeholder = 'Search for your hotel';
        autocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['displayName', 'formattedAddress'] });
          pickupInput.value = [place.displayName, place.formattedAddress].filter(Boolean).join(', ');
        });
        pickupAutocompleteHost.append(autocomplete);
        pickupAutocompleteHost.hidden = false;
        pickupInput.hidden = true;
      } catch (_error) {
        pickupAutocompleteHost.hidden = true;
        pickupInput.hidden = false;
      }
    };

    initializePickupAutocomplete();

    const packageNameFor = (variant) =>
      packageIndex >= 0 ? String(variant.options?.[packageIndex] || '') : 'Standard';
    const travelerTypeFor = (variant) =>
      travelerIndex >= 0 ? normalize(variant.options?.[travelerIndex]) : '';
    const departureTimeFor = (variant) =>
      departureIndex >= 0 ? String(variant.options?.[departureIndex] || '') : String(data.startTime || '');
    const packages = [...new Set(variants.map(packageNameFor).filter(Boolean))];

    packageSelect.replaceChildren();
    packages.forEach((packageName) => {
      const option = document.createElement('option');
      option.value = packageName;
      option.textContent = packageName;
      packageSelect.append(option);
    });

    const updateDepartureTimes = () => {
      const previousValue = departureSelect.value;
      const times = [
        ...new Set(
          variants
            .filter((variant) => packageNameFor(variant) === packageSelect.value)
            .map(departureTimeFor)
            .filter(Boolean)
        ),
      ];
      departureSelect.replaceChildren();
      times.forEach((time) => {
        const option = document.createElement('option');
        option.value = time;
        option.textContent = time;
        departureSelect.append(option);
      });
      if (times.includes(previousValue)) departureSelect.value = previousValue;
      departureWrap.hidden = !hasDepartureOption;
      departureSelect.required = hasDepartureOption;
    };

    updateDepartureTimes();

    const selectedDepartureTime = () => departureSelect.value || String(data.startTime || '');
    const hasFixedStartTime = () => /^([01]\d|2[0-3]):([0-5]\d)$/.test(selectedDepartureTime());

    const findVariant = (travelerType) =>
      variants.find(
        (variant) =>
          packageNameFor(variant) === packageSelect.value &&
          travelerTypeFor(variant) === travelerType &&
          (departureIndex < 0 || departureTimeFor(variant) === selectedDepartureTime())
      );

    const findEqualPriceFallbackVariant = () => {
      const adultVariant = findVariant('adult');
      const childVariant = findVariant('child');
      if (!adultVariant || !childVariant || adultVariant.price <= 0 || adultVariant.price !== childVariant.price) return null;
      return variants.find(
        (variant) =>
          packageNameFor(variant) === packageSelect.value &&
          variant.available &&
          variant.price === adultVariant.price
      );
    };

    const bookingTimestamp = (dateValue) => {
      const timeMatch = selectedDepartureTime().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return null;
      const [year, month, day] = dateValue.split('-').map(Number);
      // Open-entry tickets have no departure time. Use the end of the selected
      // Bangkok date so their normal booking cutoff still applies.
      const hour = timeMatch ? Number(timeMatch[1]) : 23;
      const minute = timeMatch ? Number(timeMatch[2]) : 59;
      return Date.UTC(year, month - 1, day, hour - BOOKING_TIME_ZONE_OFFSET_HOURS, minute);
    };

    const isDateBookable = (dateValue) => {
      const startsAt = bookingTimestamp(dateValue);
      if (startsAt === null || blackoutDates.has(dateValue)) return false;
      if (availableDates.size > 0 && !availableDates.has(dateValue)) return false;
      return startsAt - cutoffHours * 60 * 60 * 1000 > Date.now();
    };

    const updateMinimumDate = () => {
      const today = getBangkokDateParts();
      dateInput.min = formatDateValue(today);
      dateInput.removeAttribute('max');
      if (availableDates.size > 0) {
        const dates = [...availableDates].sort();
        const firstBookableDate = dates.find(isDateBookable);
        dateInput.min = firstBookableDate || dates[0];
        dateInput.max = dates[dates.length - 1];
        return;
      }
      for (let offset = 0; offset <= 370; offset += 1) {
        const candidate = formatDateValue(addCalendarDays(today, offset));
        if (isDateBookable(candidate)) {
          dateInput.min = candidate;
          break;
        }
      }
    };

    updateMinimumDate();

    const updateDateDisplay = () => {
      if (!dateDisplay) return;
      if (!dateInput.value) {
        dateDisplay.textContent = 'Select date';
        return;
      }
      const [year, month, day] = dateInput.value.split('-').map(Number);
      dateDisplay.textContent = new Intl.DateTimeFormat('en', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(Date.UTC(year, month - 1, day)));
    };

    updateDateDisplay();

    const openDatePicker = () => {
      if (typeof dateInput.showPicker === 'function') dateInput.showPicker();
      else dateInput.click();
    };

    datePicker?.addEventListener('click', openDatePicker);
    datePicker?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openDatePicker();
    });

    const configurationError = () => {
      if (packageIndex < 0) return 'This product is not ready for online booking. Add a “Package” product option.';
      if (travelerIndex < 0) return 'This product is not ready for online booking. Add a “Traveler type” option with Adult and Child values.';
      if (hasDepartureOption && !hasFixedStartTime()) {
        return 'This product is not ready for online booking. Add a “Departure time” option using HH:MM values.';
      }
      if (!Number.isFinite(cutoffHours) || cutoffHours <= 0) return 'This product is not ready for online booking. Add a positive booking cutoff in hours.';
      if (!packages.length) return 'This product has no bookable package.';
      const adultVariant = findVariant('adult');
      const childVariant = findVariant('child');
      if (!adultVariant || !childVariant) return 'The selected package needs both Adult and Child variants.';
      if (adultVariant.price <= 0 || childVariant.price <= 0) {
        return 'The selected package is currently unavailable for online booking.';
      }
      if ((!adultVariant.available || !childVariant.available) && !findEqualPriceFallbackVariant()) {
        return 'The selected package is currently unavailable for online booking.';
      }
      return '';
    };

    const updatePrice = () => {
      const adultVariant = findVariant('adult');
      const childVariant = findVariant('child');
      const fallbackVariant = findEqualPriceFallbackVariant();
      const adults = Math.max(0, Number.parseInt(adultsInput.value, 10) || 0);
      const children = Math.max(0, Number.parseInt(childrenInput.value, 10) || 0);
      const configMessage = configurationError();

      if (configMessage) {
        total.textContent = 'Unavailable';
        breakdown.textContent = '';
        mobileTotal.textContent = '';
        if (mobileNote) mobileNote.textContent = 'Please review the booking options';
        submitButton.disabled = true;
        showError(configMessage);
        return;
      }

      const totalCents = adultVariant.price * adults + childVariant.price * children;
      const totalLabel = money.format(totalCents / 100);
      total.textContent = totalLabel;
      mobileTotal.textContent = totalLabel;
      if (mobileNote) {
        const travelerCount = adults + children;
        mobileNote.textContent = `${travelerCount} traveler${travelerCount === 1 ? '' : 's'} selected`;
      }
      breakdown.textContent = `${adults} adult${adults === 1 ? '' : 's'} × ${money.format(adultVariant.price / 100)}` +
        (children ? ` + ${children} child${children === 1 ? '' : 'ren'} × ${money.format(childVariant.price / 100)}` : '');
      submitButton.disabled = false;
      showError('');
    };

    const validate = () => {
      const adults = Number(adultsInput.value);
      const children = childrenInput.value === '' ? 0 : Number(childrenInput.value);
      const configMessage = configurationError();
      if (configMessage) return configMessage;
      if (!Number.isInteger(adults) || adults < 1) return 'At least one adult is required.';
      if (!Number.isInteger(children) || children < 0) return 'Enter a valid number of children.';
      if (!dateInput.value) return 'Choose a travel date.';
      if (availableDates.size > 0 && !availableDates.has(dateInput.value)) return 'This experience is only available on the listed event dates.';
      if (blackoutDates.has(dateInput.value)) return 'This date is unavailable. Please choose another date.';
      if (!isDateBookable(dateInput.value)) {
        return hasFixedStartTime()
          ? `Online booking closes ${cutoffHours} hours before the ${selectedDepartureTime()} start time. Please choose a later date.`
          : `Online booking for this date has closed. Please choose a later date.`;
      }
      if (!firstNameInput?.value.trim()) return "Enter the booking person's first name.";
      if (!lastNameInput?.value.trim()) return "Enter the booking person's last name.";
      if (!phoneCodeSelect?.value) return 'Choose your international calling code.';
      const phoneDigits = phoneNumberInput?.value.replace(/\D/g, '') || '';
      const fullPhoneDigits = `${phoneCodeSelect.value.replace(/\D/g, '')}${phoneDigits}`;
      if (phoneDigits.length < 6 || fullPhoneDigits.length > 15) return 'Enter a valid contact phone number.';
      if (pickupInput && !pickupInput.value.trim()) return 'Enter your pickup hotel or area.';
      return '';
    };

    packageSelect.addEventListener('change', () => {
      updateDepartureTimes();
      updateMinimumDate();
      updatePrice();
    });
    departureSelect.addEventListener('change', () => {
      updateMinimumDate();
      updatePrice();
    });
    [adultsInput, childrenInput].forEach((input) => input.addEventListener('change', updatePrice));
    [adultsInput, childrenInput].forEach((input) => input.addEventListener('input', updatePrice));
    dateInput.addEventListener('change', () => {
      updateDateDisplay();
      if (!dateInput.value) return showError('');
      if (availableDates.size > 0 && !availableDates.has(dateInput.value)) {
        return showError('This experience is only available on the listed event dates.');
      }
      if (blackoutDates.has(dateInput.value)) return showError('This date is unavailable. Please choose another date.');
      if (!isDateBookable(dateInput.value)) {
        return showError(
          hasFixedStartTime()
            ? `Online booking closes ${cutoffHours} hours before the ${selectedDepartureTime()} start time. Please choose a later date.`
            : `Online booking for this date has closed. Please choose a later date.`
        );
      }
      showError('');
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      showError('');
      const validationMessage = validate();
      if (validationMessage) {
        showError(validationMessage);
        errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const adults = Number.parseInt(adultsInput.value, 10);
      const children = Number.parseInt(childrenInput.value, 10) || 0;
      const adultVariant = findVariant('adult');
      const childVariant = findVariant('child');
      const bookingReference = createBookingReference();
      const properties = {
        'Booking reference': bookingReference,
        'Travel date': dateInput.value,
        Package: packageSelect.value,
        Adults: String(adults),
        Children: String(children),
        'Booking first name': firstNameInput.value.trim(),
        'Booking last name': lastNameInput.value.trim(),
        'Contact phone': `${phoneCodeSelect.value} ${phoneNumberInput.value.trim()}`,
      };
      if (hasFixedStartTime()) properties['Start time'] = `${selectedDepartureTime()} Asia/Bangkok`;
      if (pickupInput?.value.trim()) properties['Pickup hotel or area'] = pickupInput.value.trim();
      if (requestsInput.value.trim()) properties['Special requests'] = requestsInput.value.trim();

      let items;
      if (adultVariant.available && (children === 0 || childVariant.available)) {
        items = [{ id: adultVariant.id, quantity: adults, properties }];
        if (children > 0) items.push({ id: childVariant.id, quantity: children, properties });
      } else {
        items = [{ id: fallbackVariant.id, quantity: adults + children, properties }];
      }

      form.setAttribute('aria-busy', 'true');
      submitButton.textContent = 'Opening secure checkout…';
      submitButton.disabled = true;

      try {
        const response = await fetch(data.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || 'The booking could not be added to your cart.');
        window.location.assign(data.checkoutUrl || '/checkout');
      } catch (error) {
        showError(`${error.message || 'The booking could not be added.'} Please try again or contact us on WhatsApp.`);
        form.removeAttribute('aria-busy');
        submitButton.textContent = 'Book now';
        submitButton.disabled = false;
      }
    });

    updatePrice();
  };

  const initialize = () => document.querySelectorAll('[data-gtp-booking-form]').forEach(initBookingForm);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
